import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import {
  AcceptedLegalDocuments,
  Mutation,
  MutationAcceptLegalDocumentsArgs,
  Query
} from 'graphql/generated/graphqlTypes';
import { ACCEPT_LEGAL_DOCUMENTS } from 'graphql/mutations';
import { GET_TENANT_LEGAL_DOCUMENTS } from 'graphql/queries';
import { useEffect } from 'react';
import { setCurrentTenantAction } from 'state/actions/currentTenant';
import { setTenantLegalDocumentsAction } from 'state/actions/tenantLegalDocuments';
import { tenantLegalDocumentsVar } from 'state/vars';
import { BaseHookProps } from '../shared/types';

const useTenantLegalDocuments = ({ handleFetchError }: BaseHookProps) => {
  const tenantLegalDocuments = useReactiveVar(tenantLegalDocumentsVar);

  const [getTenantLegalDocuments, { loading }] = useLazyQuery<Pick<Query, 'getTenantLegalDocuments'>>(
    GET_TENANT_LEGAL_DOCUMENTS,
    {
      onCompleted: (data) => {
        setTenantLegalDocumentsAction(data.getTenantLegalDocuments);
      },
      onError: () => {
        handleFetchError('Error while fetching legal documents');
      }
    }
  );

  useEffect(() => {
    getTenantLegalDocuments();
  }, [getTenantLegalDocuments]);

  const [acceptedLegalDocumentsMutation, { loading: acceptedLegalDocumentsLoading }] = useMutation<
    Pick<Mutation, 'acceptLegalDocuments'>,
    MutationAcceptLegalDocumentsArgs
  >(ACCEPT_LEGAL_DOCUMENTS, {
    onCompleted: (data) => {
      if (data.acceptLegalDocuments.__typename === 'BasicTenant') setCurrentTenantAction(data.acceptLegalDocuments);
      else if (data.acceptLegalDocuments.__typename === 'AgreementRejected')
        handleFetchError(`Error while accepting Legal Documents: ${data.acceptLegalDocuments.reason}`);
    },
    onError: () => {
      handleFetchError('Error while accepting Legal Documents');
    }
  });

  const acceptedLegalDocuments = async (acceptedLegalDocumentsVariables: AcceptedLegalDocuments) => {
    await acceptedLegalDocumentsMutation({
      variables: {
        acceptedLegalDocuments: acceptedLegalDocumentsVariables
      }
    });
  };

  return {
    tenantLegalDocuments,
    loading,
    acceptedLegalDocuments,
    acceptedLegalDocumentsLoading
  };
};

export default useTenantLegalDocuments;
